<template>
  <HLContentWrap full-screen namespace="kaizen">
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
      <!-- Loading Screen -->
      <div v-if="loading" class="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>

      <!-- Main Application -->
      <div v-else>
        <!-- Authentication Pages (Login, Register, Forgot Password, etc.) -->
        <router-view v-if="!isAuthenticated || isAuthPage" />

        <!-- Main Layout (Dashboard and other authenticated pages) -->
        <div v-else class="flex h-screen">
          <!-- Main Content -->
          <div class="flex-1 flex flex-col overflow-hidden relative">
            <!-- Top Navigation - Hidden on project pages, fixed on others -->
            <TopNav v-if="!isProjectDetailPage" />

            <!-- Page Content -->
            <main
              class="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-all duration-300">
              <div class="h-full">
                <router-view />
              </div>
            </main>
          </div>
        </div>
      </div>

      <!-- Global Confirmation Dialog -->
      <ConfirmationDialog :isOpen="confirmation.isOpen.value" :title="confirmation.config.title"
        :description="confirmation.config.description" :details="confirmation.config.details"
        :warningText="confirmation.config.warningText" :confirmText="confirmation.config.confirmText"
        :cancelText="confirmation.config.cancelText" :confirmVariant="confirmation.config.confirmVariant"
        :cancelVariant="confirmation.config.cancelVariant" :iconType="confirmation.config.iconType"
        :showIcon="confirmation.config.showIcon" :showCancel="confirmation.config.showCancel"
        :size="confirmation.config.size" :closable="confirmation.config.closable" :loading="confirmation.loading.value"
        :requireInput="confirmation.config.requireInput" :inputLabel="confirmation.config.inputLabel"
        :inputPlaceholder="confirmation.config.inputPlaceholder" :inputType="confirmation.config.inputType"
        :inputHelper="confirmation.config.inputHelper" :expectedInput="confirmation.config.expectedInput"
        @confirm="confirmation.handleConfirm" @cancel="confirmation.handleCancel" @close="confirmation.handleClose" />
    </div>
  </HLContentWrap>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useGlobalConfirmation } from '@/composables/useConfirmation'
import TopNav from '@/components/ui/TopNav.vue'
import ConfirmationDialog from '@/components/ui/ConfirmationDialog.vue'
import '@platform-ui/highrise/style.css'
import {HLContentWrap} from '@platform-ui/highrise'
// Stores
const authStore = useAuthStore()
const themeStore = useThemeStore()

// Global confirmation
const confirmation = useGlobalConfirmation()

// Router
const route = useRoute()

// Reactive data
const loading = ref(true)

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)

// Check if current route is an auth page (login, register, etc.)
const isAuthPage = computed(() => {
  const authRoutes = ['Login', 'Register', 'ForgotPassword', 'ResetPassword']
  return authRoutes.includes(route.name)
})

// Check if current route is a project-related page
// Hide global TopNav on all project routes since ProjectDetail provides its own TopNav with project name
const isProjectDetailPage = computed(() => {
  const projectRoutes = [
    // Project main pages
    'ProjectDetail',
    'ProjectOverview',
    'ProjectFeatures',
    'ProjectTestCases',
    'ProjectTestRuns',
    'ProjectBugs',
    'ProjectSettings',
    'ProjectTeam',
    // Feature pages
    'CreateFeature',
    'FeatureDetail',
    // Test Case pages
    'CreateTestCase',
    'TestCaseDetail',
    // Test Run pages
    'CreateTestRun',
    'EditTestRun',
    'TestRunDetail',
    'ProjectTestRunDetail',
    // Bug pages
    'CreateBug',
    'BugDetail',
    'ProjectBugDetail',
    // Deleted pages
    'DeletedTestRuns'
  ]
  return projectRoutes.includes(route.name)
})

// Check if sidebar should be shown based on current route
const showSidebar = computed(() => {
  // Sidebar is not required for any page
  return false
})

// Methods
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const scrollToTop = () => {
  const mainContainer = document.querySelector('.flex-1.overflow-x-hidden.overflow-y-auto')
  if (mainContainer) {
    mainContainer.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Watch for route changes to scroll to top
watch(() => route.name, () => {
  scrollToTop()
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  try {
    // Initialize theme
    themeStore.initializeTheme()

    // Initialize authentication
    await authStore.initializeAuth()
  } catch (error) {
    console.error('App initialization error:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style>
/* Global styles */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark ::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Focus styles */
.focus-ring {
  @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900;
}

/* Transition utilities */
.transition-all {
  transition: all 0.2s ease-in-out;
}

.transition-colors {
  transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
}

.transition-transform {
  transition: transform 0.2s ease-in-out;
}

.transition-opacity {
  transition: opacity 0.2s ease-in-out;
}

/* Reduce backdrop blur for all modals - minimal to no blur */
.n-modal-mask,
.n-modal-mask-wrapper,
.hr-modal-mask,
.hr-modal-backdrop,
[class*="modal-mask"],
[class*="modal-backdrop"],
[class*="n-base"],
.n-base,
.hr-modal {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
  filter: blur(0px) !important;
}

/* Target any element with inline backdrop-filter style */
body [style*="backdrop-filter"],
body [style*="backdropFilter"] {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
}

/* Ensure modal overlays have minimal blur */
[class*="overlay"],
[data-modal-overlay] {
  backdrop-filter: blur(0px) !important;
  -webkit-backdrop-filter: blur(0px) !important;
}
</style>